/**
 * Public campaign discovery — one shared read of GET /api/campaigns/public.
 *
 * The response carries `visible` (enough live public campaigns AND the
 * founder switch on). Navbar, the landing "Meet the students" section and
 * /campaigns all render nothing donor-facing until it flips. Module-level
 * cache so the three consumers share one request per minute.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TTL_MS = 60 * 1000;
const EMPTY = { visible: false, ready: false, enabled: true, count: 0, min_required: 3, items: [], lead_ids: [], schools: [], categories: [] };

let cache = { at: 0, value: null, promise: null };

export const fetchDiscovery = async ({ force = false } = {}) => {
  const fresh = cache.value && Date.now() - cache.at < TTL_MS;
  if (fresh && !force) return cache.value;
  if (cache.promise && !force) return cache.promise;
  cache.promise = axios
    .get(`${API}/campaigns/public`)
    .then((res) => {
      cache = { at: Date.now(), value: res.data, promise: null };
      return res.data;
    })
    .catch(() => {
      cache.promise = null;
      return cache.value || EMPTY;
    });
  return cache.promise;
};

export const useDiscovery = () => {
  const [data, setData] = useState(cache.value || EMPTY);
  const [loading, setLoading] = useState(!cache.value);
  useEffect(() => {
    let alive = true;
    fetchDiscovery().then((d) => { if (alive) { setData(d); setLoading(false); } });
    return () => { alive = false; };
  }, []);
  return { ...data, loading };
};

export const daysLeft = (iso) => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.ceil((t - Date.now()) / 86400000));
};

/** Public file URL; pass `width` (240 | 480 | 960) for a cached downscaled WebP rendition. */
export const publicFileUrl = (fileId, width) =>
  fileId ? `${API}/files/${fileId}${width ? `?w=${width}` : ""}` : null;

/** srcSet for grid covers: the browser picks 480 or 960 px by viewport + pixel density. */
export const coverSrcSet = (fileId) =>
  fileId ? `${publicFileUrl(fileId, 480)} 480w, ${publicFileUrl(fileId, 960)} 960w` : undefined;
